import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Formik, Form } from "formik";
import {
  GET_LEAVING_QUESTIONNAIRE,
  GET_LEAVING_QUESTIONNAIRES,
  UPDATE_LEAVING_QUESTIONNAIRE,
} from "../gql";
import { Button, Box, Container, Card, CardContent } from "@mui/material";
import Grid from "@mui/material/Grid2";
import LoadingCircle from "../../../../components/LoadingCircle";
import QuestionnaireGenerator from "./QuestionnaireGenerator";
import defaultInitialValues from "./FormInitialValues";
import UpdateSnackBar from "../../../../components/UpdateSnackBar";
import { isQuestionnaireDisabled } from "../../../../utils/QuestionnaireUtils";
import QuestionnaireStateMessage from "../../../../components/QuestionnaireStateMessage";
import ErrorLayout from "../../../../components/layout/ErrorLayout";
import { useNavigate, useParams } from "react-router-dom";
import { SnackBarType } from "../../../../utils/Types/SnackBarType";
import { SubmitFlagType } from "../../../../utils/Types/SubmitFlagType";

const LeavingForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [qst, setQst] = useState<any>();

  const [snackBar, setSnackBar] = useState<SnackBarType>({
    message: "",
    severity: "info",
    open: false,
  });
  const [validationFields, setValidationFields] = useState<any[]>([]);

  const { loading, error, data } = useQuery(GET_LEAVING_QUESTIONNAIRE, {
    variables: { questionnaireId: id ? parseInt(id, 10) : undefined },
    skip: !id,
  });

  const [updateLeavingQuestionnaire, { error: updateDataError }] = useMutation(
    UPDATE_LEAVING_QUESTIONNAIRE,
    {
      refetchQueries: [{ query: GET_LEAVING_QUESTIONNAIRES }],
      awaitRefetchQueries: true,
      update: (cache, updatedQuestionnaire) => {
        cache.writeQuery({
          query: GET_LEAVING_QUESTIONNAIRE,
          variables: { questionnaireId: id ? parseInt(id, 10) : undefined },
          data: {
            leavingQuestionnaire:
              updatedQuestionnaire.data.leavingQuestionnaireUpdateClient,
          },
        });
      },
      onCompleted: (updatedData) => {
        if (process.env.REACT_APP_ENV_TYPE !== "PRODUCTION") {
          console.log("UPDATED");
          console.log(updatedData);
        }

        const newData = updatedData.leavingQuestionnaireUpdateClient;
        setQst(newData);

        if (newData.state === "sent") {
          navigate("/dotaznikvystup");
        }

        setSnackBar({ open: true, message: "Uloženo", severity: "success" });
      },
    }
  );

  // useEffect(() => {
  //   if (data) {
  //     if (process.env.REACT_APP_ENV_TYPE !== "PRODUCTION") {
  //       console.log(data.leavingQuestionnaire);
  //       setQst(data.leavingQuestionnaire);

  //       setValidationFields(
  //         parseFieldsToValidate(
  //           JSON.parse(data.leavingQuestionnaire.questionnaireConfig)
  //         )
  //       );
  //     }
  //   }

  //   setSnackBar({ open: true, message: "Uloženo", severity: "success" });
  // }, [data]);

  useEffect(() => {
    if (data) {
      if (process.env.REACT_APP_ENV_TYPE !== "PRODUCTION") {
        console.log(data.leavingQuestionnaire);
      }

      setQst(data.leavingQuestionnaire);
      setValidationFields(
        parseFieldsToValidate(
          JSON.parse(data.leavingQuestionnaire.questionnaireConfig)
        )
      );
    }
  }, [data]);

  const parseFieldsToValidate = (qstConfig: any) => {
    const fieldList = [];

    for (let key in qstConfig) {
      const item = qstConfig[key];

      if (item.name && item.isRequired) {
        fieldList.push(item.name);
      }
    }

    console.log(fieldList);
    return fieldList;
  };

  const getInitialValues = () => {
    let initials: any = {};

    let keys = Object.keys(defaultInitialValues);

    keys.map((key: any) => {
      const item = qst[key];

      if (item === null) {
        initials[key] = null;
      } else {
        initials[key] = item;
      }

      return "";
    });

    return initials;
  };

  const validation = (vals: any) => {
    const errors: any = {};
    const requiredLabel = "Musí být vyplněno";

    validationFields.forEach((item: any) => {
      if (!vals[item]) {
        errors[item] = requiredLabel;
      }
    });

    console.log(errors);

    return errors;
  };

  const handleSnackBarClose = () => {
    setSnackBar({ open: false, severity: "info", message: "" });
  };

  const buildMutationInput = (values: any) => {
    const input: Record<string, unknown> = {
      questionnaireId: id ? parseInt(id, 10) : undefined,
      state: values.state,
      questionnaireConfig: values.questionnaireConfig,
      customText: values.customText,
      questionnaireResult: values.questionnaireResult,
    };

    // Copy answer1-answer20 from values (from initial load)
    for (let i = 1; i <= 20; i++) {
      const key = `answer${i}`;
      if (values[key] !== undefined) input[key] = values[key];
    }

    // Override with values from questionnaireConfig (FieldFactoryLeaving stores user input there)
    try {
      const config =
        typeof values.questionnaireConfig === "string"
          ? JSON.parse(values.questionnaireConfig)
          : values.questionnaireConfig;
      if (Array.isArray(config)) {
        for (const field of config) {
          if (field?.name?.startsWith("answer")) {
            const val = field.val;
            input[field.name] =
              typeof val === "object" && val !== null && "value" in val
                ? val.value
                : val;
          }
        }
      }
    } catch {
      // config parse failed, keep answer1-20 from values above
    }

    return input;
  };

  const handleSubmit = (
    values: any,
    flag: SubmitFlagType,
    setSubmitting: any
  ) => {
    setSubmitting(true);

    const newValues = buildMutationInput(values);

    if (flag === "sent") {
      newValues.state = "sent";
    }

    updateLeavingQuestionnaire({
      variables: { input: newValues },
    }).then(() => {
      setSubmitting(false);
    });
  };

  if (updateDataError)
    return <ErrorLayout errorMsg={updateDataError.message} />;
  if (!id) return <ErrorLayout errorMsg="Chybí ID dotazníku" />;
  if (error) return <ErrorLayout errorMsg={error.message || ""} />;
  if (loading) return <LoadingCircle />;
  if (!qst) return <LoadingCircle />;

  // if (Object.keys(qst).length === 0) return <LoadingCircle />;

  return (
    <>
      <Formik
        initialValues={getInitialValues()}
        validate={validation}
        enableReinitialize
        onSubmit={() => { }}
      >
        {({ isSubmitting, values, setSubmitting, isValid, dirty }) => (
          <Form>
            <Container maxWidth="lg">
              <Grid spacing={3} container justifyContent={"center"}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <QuestionnaireStateMessage state={qst.state} />
                      <Grid size={{ xs: 12 }}>
                        <QuestionnaireGenerator
                          config={
                            values.questionnaireConfig.length > 0
                              ? values.questionnaireConfig
                              : qst.questionnaireConfig
                          }
                          isDisabled={isQuestionnaireDisabled(qst.state)}
                        />

                        <p style={{
                          fontWeight: 700,
                        }}>
                          Chceš nadále dostávat informace ze světa Seznamu?
                          <br />
                          Zaregistruj se{" "}
                          <a
                            href="https://o-seznam.cz/kariera/#newsletter"
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              color: "#0077cc",
                            }}
                          >
                            zde
                          </a>.
                        </p>
                      </Grid>

                      {!isQuestionnaireDisabled(qst.state) && (
                        <div>
                          {!isSubmitting && (
                            <>
                              <Box mb={2}>
                                <Button
                                  disableElevation
                                  fullWidth
                                  variant={"contained"}
                                  onClick={() =>
                                    handleSubmit(
                                      values,
                                      "update",
                                      setSubmitting
                                    )
                                  }
                                  disabled={!dirty || isSubmitting}
                                >
                                  Uložit
                                </Button>
                              </Box>
                              <Button
                                fullWidth
                                disableElevation
                                variant={"contained"}
                                color="primary"
                                onClick={() =>
                                  handleSubmit(values, "sent", setSubmitting)
                                }
                                disabled={!isValid || isSubmitting}
                              >
                                Odeslat
                              </Button>
                            </>
                          )}
                          {isSubmitting && <>{LoadingCircle}</>}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Container>
          </Form>
        )}
      </Formik>
      <UpdateSnackBar
        message={snackBar.message}
        onClose={handleSnackBarClose}
        isOpen={snackBar.open}
        severity={snackBar.severity}
      />
    </>
  );
};

export default LeavingForm;
